import { useState } from "react";
import { Monitor, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SECTION_META, estimateMinutes } from "@/lib/library";
import { SandboxPanel } from "@/pages/assessments/builder/SandboxPanel";

const SCALE = ["Very inaccurate", "Moderately inaccurate", "Neither", "Moderately accurate", "Very accurate"];

function PreviewQuestion({ q, n }) {
  return (
    <div className="space-y-2" data-testid={`preview-q-${q.id}`}>
      <p className="font-medium">{n}. {q.prompt || <span className="text-ink-muted">(No question text yet)</span>}{q.required && <span className="text-risk"> *</span>}</p>
      {["single", "multi", "yesno"].includes(q.kind) ? (
        <ul className="space-y-1.5">{q.options.map((o) => (
          <li key={o.id} className="flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm">
            <input type={q.kind === "multi" ? "checkbox" : "radio"} disabled aria-label={o.text} /> {o.text || "…"}</li>))}</ul>
      ) : <div className={`rounded-lg border border-line bg-surface-2 ${q.kind === "text" ? "h-24" : "h-10"}`} aria-hidden />}
    </div>
  );
}

function SectionPreview({ s }) {
  if (s.type === "personality") return (
    <div className="space-y-2 text-sm">
      <p className="text-ink-secondary">Rate how well each statement describes you. Example:</p>
      <p className="font-medium">"I make plans and stick to them."</p>
      <div className="flex flex-wrap gap-1.5">{SCALE.map((x) => <span key={x} className="rounded-full border border-line px-2.5 py-1 text-xs">{x}</span>)}</div>
    </div>
  );
  if (s.type === "scenarios") return s.scenarios.length ? (
    <div className="space-y-2 text-sm"><p className="font-medium">{s.scenarios[0].scenario}</p>
      <ul className="space-y-1.5">{s.scenarios[0].options.map((o) => <li key={o.id} className="rounded-lg border border-line px-3 py-2">{o.text || "…"}</li>)}</ul>
      {s.scenarios.length > 1 && <p className="text-xs text-ink-muted">+ {s.scenarios.length - 1} more scenario(s)</p>}</div>
  ) : <p className="text-sm text-ink-secondary">Candidates read short work situations written for the job and pick the best way to respond.</p>;
  return <div className="space-y-5">{s.questions.map((q, i) => <PreviewQuestion key={q.id} q={q} n={i + 1} />)}</div>;
}

export function PreviewStep({ t, canEdit, templateId }) {
  const [mobile, setMobile] = useState(false);
  return (
    <div className="space-y-4" data-testid="builder-preview" data-tour="assessment-preview">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-ink-secondary">This is what candidates see.</p>
        <span className="ml-auto flex gap-1" role="group" aria-label="Preview size">
          <Button size="sm" variant={mobile ? "outline" : "default"} onClick={() => setMobile(false)} aria-pressed={!mobile} data-testid="preview-desktop"><Monitor size={14} /> Desktop</Button>
          <Button size="sm" variant={mobile ? "default" : "outline"} onClick={() => setMobile(true)} aria-pressed={mobile} data-testid="preview-mobile"><Smartphone size={14} /> Phone</Button>
        </span>
      </div>
      {canEdit && <SandboxPanel templateId={templateId} />}
      <div className={`mx-auto space-y-6 rounded-[6px] border border-line bg-white p-6 shadow-sm transition-[max-width] duration-300 ${mobile ? "max-w-[390px]" : "max-w-3xl"}`} data-testid="preview-frame">
        <header className="space-y-1 border-b border-line pb-4">
          <h2 className="font-display text-xl font-semibold">{t.name || "Untitled assessment"}</h2>
          {t.description && <p className="text-sm text-ink-secondary">{t.description}</p>}
          <p className="text-xs text-ink-muted">About {estimateMinutes(t.sections)} minutes · {t.sections.length} part{t.sections.length !== 1 && "s"}{t.time_limit_min ? ` · ${t.time_limit_min}-minute limit` : " · no time limit"} · your progress is saved</p>
        </header>
        {t.sections.map((s, i) => (
          <section key={s.id} className="space-y-3" data-testid={`preview-section-${i}`}>
            <h3 className="text-base font-semibold">Part {i + 1}: {s.title || SECTION_META[s.type].label}</h3>
            <SectionPreview s={s} />
          </section>
        ))}
      </div>
    </div>
  );
}
